import React, {useContext, useEffect, useRef, useState} from 'react';
import {UserInfoType} from '../Types/UserInfoType';
import {STORAGE_KEYS} from '../Constants/AppConstants';
import {decode} from 'base-64';
import LocalStorageService from '../Services/LocalStorageService';
import {User} from 'oidc-client-ts';
import useEHSAuth from '../Hooks/useEHSAuth';

type PowerSyncUserProviderProps = {
  children: React.ReactNode;
};

type PowerSyncUserContextValue = {
  loadTokenData: () => Promise<void>;
  logOut: () => Promise<void>;
  setLogout: (appLogOut: () => Promise<void>) => void;
  getUserInfo: () => UserInfoType;
  setUserInfo: (
    email: string,
    orgId: number,
    orgName: string,
    personName: string,
    personId: string,
  ) => void;
};

const PowerSyncUserContext = React.createContext<PowerSyncUserContextValue>(
  {} as PowerSyncUserContextValue,
);

//Mongo Permissions table has list of permissions with an array of roles that has access to that permission
const PowerSyncUserProvider: React.FunctionComponent<
  PowerSyncUserProviderProps
> = ({children}): React.ReactElement => {
  const [email, setEmail] = useState('');
  const [personId, setPersonId] = useState('');
  const [personName, setPersonName] = useState('');
  const [orgId, setOrgId] = useState(0);
  const [orgName, setOrgName] = useState('');
  const {getUser} = useEHSAuth();

  let storageService = new LocalStorageService();

  useEffect(() => {
    loadTokenData();
  }, []);

  let _appLogoutRef = useRef<(() => Promise<void>) | null>(null);

  const loadTokenData = async () => {
    if (!email || !personId || !personName || !orgId || !orgName) {
      let token = getUser().access_token;

      if (token) {
        let split = token.split('.');
        let decodedPayload = JSON.parse(decode(split[1]));
        setPersonName(decodedPayload.name);
        setEmail(decodedPayload.username);
        setPersonId(decodedPayload.personId);
        setOrgId(decodedPayload.orgId);
        let organisation = JSON.parse(decodedPayload.organisation);
        setOrgName(organisation.Name);
      }
    }
  };

  const logOut = async () => {
    if (_appLogoutRef.current) {
      setEmail('');
      setOrgId(0);
      setOrgName('');
      setPersonName('');
      setPersonId('');

      storageService.set(STORAGE_KEYS.TOKEN_KEY, '');
      storageService.set(STORAGE_KEYS.REFRESH_TOKEN_KEY, '');
      await _appLogoutRef.current();
    }
  };

  const setLogout = (appLogOut: () => Promise<void>) => {
    _appLogoutRef.current = appLogOut;
  };

  const setUserInfo = (
    email: string,
    orgId: number,
    orgName: string,
    personName: string,
    personId: string,
  ) => {
    setEmail(email);
    setOrgId(orgId);
    setOrgName(orgName);
    setPersonName(personName);
    setPersonId(personId);
  };

  const getUserInfo = (): UserInfoType => {
    return {
      email: email,
      orgId: orgId,
      personId: personId,
      orgName: orgName,
      personName: personName,
    };
  };

  const value = {
    loadTokenData: loadTokenData,
    logOut: logOut,
    setLogout: setLogout,
    getUserInfo: getUserInfo,
    setUserInfo: setUserInfo,
  };

  return (
    <PowerSyncUserContext.Provider value={value}>
      {children}
    </PowerSyncUserContext.Provider>
  );
};

const usePowerSyncUser = () => {
  const powerSyncUserContext = useContext(PowerSyncUserContext);

  if (powerSyncUserContext === null)
    throw new Error(
      'usePowerSyncUser() called outside of a PowerSyncUserProvider?',
    );

  return powerSyncUserContext;
};

export {PowerSyncUserProvider, PowerSyncUserContext, usePowerSyncUser};
